import React, { memo, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { buildCashflowMonthlyRowData, buildCashflowRowData, exportRowsToCsv } from '../utils/comparisonUtils';
import { columnHasData, formatCouponPercent, isCouponField } from '../utils/cashflowDisplay';

// `gated` metrics are genuinely zero for most bonds we hold (same rule as
// the cashflow table/grid: cashflowDisplay.js's columnHasData) - offered
// only when the currently charted rows actually carry a value. COUPON is a
// rate, not a dollar amount: never gated (always populated) and formatted
// as a percentage, never summed.
const METRICS = [
  { value: 'PRINCIPAL', label: 'Principal' },
  { value: 'INTEREST', label: 'Interest' },
  { value: 'CASHFLOW', label: 'Total Cashflow' },
  { value: 'BALANCE', label: 'Balance' },
  { value: 'COUPON', label: 'Coupon' },
  { value: 'WRITEDOWN', label: 'Writedown', gated: true },
  { value: 'INTEREST_SHORTFALL', label: 'Interest Shortfall', gated: true },
  { value: 'PREPAY_PENALTY', label: 'Prepay Penalty', gated: true },
];

const SERIES_COLORS = [
  '#2563eb',
  '#dc2626',
  '#16a34a',
  '#9333ea',
  '#ea580c',
  '#0891b2',
  '#ca8a04',
  '#db2777',
];

const CashflowComparisonChart = memo(({
  scenarios,
  scenarioLabels = Object.keys(scenarios || {}),
  selectedTranche,
  selectedCusip = '',
  grain = 'year',
  height = 400,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [selectedMetric, setSelectedMetric] = useState('CASHFLOW');
  const isMonthly = grain === 'month';
  const periodField = isMonthly ? 'DATE' : 'YEAR';

  const chartData = useMemo(
    () => (isMonthly ? buildCashflowMonthlyRowData : buildCashflowRowData)(
      scenarios,
      scenarioLabels,
      selectedTranche,
      selectedCusip
    ),
    [isMonthly, scenarioLabels, scenarios, selectedCusip, selectedTranche]
  );

  // Gated metrics (writedown/shortfall/penalty) are offered only when some
  // displayed row, for some scenario, actually carries a value - same
  // columnHasData gate the cashflow table uses, so a bond with none of those
  // events doesn't get a dead menu entry.
  const availableMetrics = useMemo(
    () => METRICS.filter(metric => (
      !metric.gated || scenarioLabels.some(label => columnHasData(chartData, `${label}__${metric.value}`))
    )),
    [chartData, scenarioLabels]
  );

  // A metric can drop out from under the current selection (grain switch,
  // tranche/CUSIP change) - fall back to the always-available default.
  useEffect(() => {
    if (!availableMetrics.some(metric => metric.value === selectedMetric)) {
      setSelectedMetric('CASHFLOW');
    }
  }, [availableMetrics, selectedMetric]);

  const isCoupon = isCouponField(selectedMetric);

  const hasValues = chartData.some(row => scenarioLabels.some(
    label => row[`${label}__${selectedMetric}`] !== null
      && row[`${label}__${selectedMetric}`] !== undefined
  ));

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{ px: 1.5, py: 1.25, mb: 2, borderRadius: 2.5, display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mr: 'auto' }}>
          Cashflow field
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={selectedMetric}
          onChange={(_, value) => value && setSelectedMetric(value)}
          aria-label="Cashflow field"
        >
          {availableMetrics.map(metric => (
            <ToggleButton
              key={metric.value}
              value={metric.value}
              aria-label={metric.label}
            >
              {metric.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Button
          size="small"
          variant="outlined"
          disabled={!hasValues}
          onClick={() => exportRowsToCsv(chartData, `cashflow_chart_${selectedMetric}`)}
        >
          Export CSV
        </Button>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, minHeight: height }}>
        {hasValues ? (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={isDark ? theme.palette.divider : '#e2e8f0'}
              />
              <XAxis
                dataKey={periodField}
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => (isCoupon
                  ? formatCouponPercent(value)
                  : Number(value).toLocaleString(undefined, {
                    notation: 'compact',
                    maximumFractionDigits: 1,
                  }))}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                  color: theme.palette.text.primary,
                }}
                formatter={(value, name) => [isCoupon
                  ? formatCouponPercent(value)
                  : Number(value).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }), name]}
              />
              <Legend wrapperStyle={{ color: theme.palette.text.primary }} />
              {scenarioLabels.map((label, index) => (
                <Line
                  key={label}
                  type="monotone"
                  dataKey={`${label}__${selectedMetric}`}
                  name={label}
                  stroke={SERIES_COLORS[index % SERIES_COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
              {chartData.length > 1 && (
                <Brush
                  dataKey={periodField}
                  height={22}
                  travellerWidth={8}
                  stroke={theme.palette.primary.main}
                  fill={theme.palette.background.paper}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height,
              color: theme.palette.text.secondary,
            }}
          >
            <Typography variant="body1">
              No cashflow available for the selected tranche, CUSIP, and field
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
});

CashflowComparisonChart.displayName = 'CashflowComparisonChart';

export default CashflowComparisonChart;
